import { BehaviorSubject } from "rxjs";
import { Injectable, signal } from "@angular/core";
import { SiteModel } from "src/app/store/models/asset.model";

@Injectable({ providedIn: "root" })
export class CheckinMapService {
  // ** Variables ** //
  selectedCountry: string = "";
  selectedSites: SiteModel[] = [];
  showSitePolygons: boolean = true;
  isCheckinModal = new BehaviorSubject<boolean>(false);
  selectedSite = new BehaviorSubject<SiteModel | null>(null);
  mapType = signal<"styled_map" | "satellite">("styled_map");

  public readonly GROUPS = {
    AGGREGATES: "Aggregates",
    CEMENT: "Cement",
    READY_MIX: "Ready Mix Concrete",
    SUPPRORT: "Support Processes",
    SOLUTIONS: "Solutions and Products",
  } as const;

  private readonly DEFAULT_GROUP = this.GROUPS.SOLUTIONS;

  private readonly SEGMENT_GROUP_MAP: Record<string, string> = {
    // Aggregates
    "AGG-Aggregates": this.GROUPS.AGGREGATES,

    // Ready Mix Concrete
    "RMX-ReadyMix Concrete": this.GROUPS.READY_MIX,

    // Cement
    "TRD-Trading": this.GROUPS.CEMENT,
    "AFR-Geocycle": this.GROUPS.CEMENT,
    "CLC-Clinker & Cement": this.GROUPS.CEMENT,
    "MIC-Mineral Cpts & Other Cem Mats": this.GROUPS.CEMENT,

    // Solutions and Products
    "ASP-Asphalt": this.GROUPS.SOLUTIONS,
    "DRM-Mortar Solutions": this.GROUPS.SOLUTIONS,
    "CPR-Concrete Products": this.GROUPS.SOLUTIONS,
    "RFG-Roofing / Coating": this.GROUPS.SOLUTIONS,
    "COP-Construction / Paving": this.GROUPS.SOLUTIONS,
    "OSP-Other Solutions and Products": this.GROUPS.SOLUTIONS,

    // Support Processes
    "RET-Retail": this.GROUPS.SUPPRORT,
    "COR-Corporate Offices": this.GROUPS.SUPPRORT,
  };

  private readonly GROUP_ICON_MAP: Record<string, string> = {
    [this.GROUPS.READY_MIX]: "../assets/check-in/icons/readymix.svg",
    [this.GROUPS.AGGREGATES]: "../assets/check-in/icons/aggregates.svg",
    [this.GROUPS.CEMENT]: "../assets/check-in/icons/clinker&cement.svg",
    [this.GROUPS.SUPPRORT]: "../assets/check-in/icons/supportProcesses.svg",
    [this.GROUPS.SOLUTIONS]: "../assets/check-in/icons/solutions&products.svg",
  };

  private readonly COLOR_CHANGED_GROUP_ICON_MAP: Record<string, string> = {
    [this.GROUPS.READY_MIX]:
      "../assets/check-in/business-segments/readymix.svg",
    [this.GROUPS.AGGREGATES]:
      "../assets/check-in/business-segments/aggregates.svg",
    [this.GROUPS.CEMENT]:
      "../assets/check-in/business-segments/clinker&cement.svg",
    [this.GROUPS.SUPPRORT]:
      "../assets/check-in/business-segments/supportProcesses.svg",
    [this.GROUPS.SOLUTIONS]:
      "../assets/check-in/business-segments/solutions&products.svg",
  };

  // ** Variables ** //
  countries: Record<string, string> = {
    cuba: "Cuba",
    fiji: "Fiji",
    iran: "Iran",
    iraq: "Iraq",
    laos: "Laos",
    mali: "Mali",
    oman: "Oman",
    peru: "Peru",
    chad: "Chad",
    togo: "Togo",
    benin: "Benin",
    chile: "Chile",
    china: "China",
    egypt: "Egypt",
    spain: "Spain",
    gabon: "Gabon",
    ghana: "Ghana",
    haiti: "Haiti",
    india: "India",
    italy: "Italy",
    japan: "Japan",
    kenya: "Kenya",
    libya: "Libya",
    malta: "Malta",
    niger: "Niger",
    nepal: "Nepal",
    qatar: "Qatar",
    sudan: "Sudan",
    syria: "Syria",
    yemen: "Yemen",
    quatar: "Qatar",
    angola: "Angola",
    belize: "Belize",
    brazil: "Brazil",
    brunei: "Brunei",
    bhutan: "Bhutan",
    canada: "Canada",
    cyprus: "Cyprus",
    france: "France",
    guinea: "Guinea",
    gambia: "Gambia",
    greece: "Greece",
    guyana: "Guyana",
    israel: "Israel",
    jordan: "Jordan",
    kosovo: "Kosovo",
    kuwait: "Kuwait",
    latvia: "Latvia",
    mexico: "Mexico",
    burma: "Myanmar",
    malawi: "Malawi",
    norway: "Norway",
    panama: "Panama",
    poland: "Poland",
    russia: "Russia",
    rwanda: "Rwanda",
    sweden: "Sweden",
    turkey: "Turkey",
    taiwan: "Taiwan",
    uganda: "Uganda",
    zambia: "Zambia",
    albania: "Albania",
    armenia: "Armenia",
    austria: "Austria",
    burundi: "Burundi",
    belgium: "Belgium",
    belarus: "Belarus",
    bermuda: "Bermuda",
    bolivia: "Bolivia",
    germany: "Germany",
    denmark: "Denmark",
    algeria: "Algeria",
    ecuador: "Ecuador",
    eritrea: "Eritrea",
    estonia: "Estonia",
    finland: "Finland",
    georgia: "Georgia",
    croatia: "Croatia",
    hungary: "Hungary",
    ireland: "Ireland",
    iceland: "Iceland",
    jamaica: "Jamaica",
    lebanon: "Lebanon",
    liberia: "Liberia",
    lesotho: "Lesotho",
    morocco: "Morocco",
    moldova: "Moldova",
    myanmar: "Myanmar",
    namibia: "Namibia",
    nigeria: "Nigeria",
    romania: "Romania",
    senegal: "Senegal",
    somalia: "Somalia",
    tunisia: "Tunisia",
    ukraine: "Ukraine",
    uruguay: "Uruguay",
    vietnam: "Vietnam",
    vanuatu: "Vanuatu",
    moldavia: "Moldova",
    bulgaria: "Bulgaria",
    botswana: "Botswana",
    cameroon: "Cameroon",
    colombia: "Colombia",
    djibouti: "Djibouti",
    ethiopia: "Ethiopia",
    uk: "United Kingdom",
    honduras: "Honduras",
    cambodia: "Cambodia",
    korea: "South Korea",
    mongolia: "Mongolia",
    malaysia: "Malaysia",
    pakistan: "Pakistan",
    portugal: "Portugal",
    paraguay: "Paraguay",
    suriname: "Suriname",
    slovakia: "Slovakia",
    slovenia: "Slovenia",
    thailand: "Thailand",
    zimbabwe: "Zimbabwe",
    argentina: "Argentina",
    australia: "Australia",
    bahamas: "The Bahamas",
    greenland: "Greenland",
    guatemala: "Guatemala",
    indonesia: "Indonesia",
    lithuania: "Lithuania",
    macedonia: "Macedonia",
    nicaragua: "Nicaragua",
    holland: "Netherlands",
    swaziland: "Swaziland",
    venezuela: "Venezuela",
    antarctica: "Antarctica",
    azerbaijan: "Azerbaijan",
    bangladesh: "Bangladesh",
    kazakhstan: "Kazakhstan",
    kyrgyzstan: "Kyrgyzstan",
    "sri lanka": "Sri Lanka",
    luxembourg: "Luxembourg",
    madagascar: "Madagascar",
    montenegro: "Montenegro",
    mozambique: "Mozambique",
    mauritania: "Mauritania",
    somaliland: "Somaliland",
    tajikistan: "Tajikistan",
    uzbekistan: "Uzbekistan",
    "west bank": "West Bank",
    czechia: "Czech Republic",
    britain: "United Kingdom",
    afghanistan: "Afghanistan",
    switzerland: "Switzerland",
    "costa rica": "Costa Rica",
    netherlands: "Netherlands",
    philippines: "Philippines",
    "east timor": "East Timor",
    uae: "United Arab Emirates",
    "the bahamas": "The Bahamas",
    "ivory coast": "Ivory Coast",
    "south korea": "South Korea",
    "new zealand": "New Zealand",
    "puerto rico": "Puerto Rico",
    "north korea": "North Korea",
    "south sudan": "South Sudan",
    "el salvador": "El Salvador",
    serbia: "Republic of Serbia",
    turkmenistan: "Turkmenistan",
    "burkina faso": "Burkina Faso",
    "cote d ivoire": "Ivory Coast",
    congo: "Republic of the Congo",
    "saudi arabia": "Saudi Arabia",
    "sierra leone": "Sierra Leone",
    "south africa": "South Africa",
    usa: "United States of America",
    "guinea bissau": "Guinea Bissau",
    "french guiana": "French Guiana",
    "new caledonia": "New Caledonia",
    "czech republic": "Czech Republic",
    "united kingdom": "United Kingdom",
    "western sahara": "Western Sahara",
    "northern cyprus": "Northern Cyprus",
    "solomon islands": "Solomon Islands",
    "falkland islands": "Falkland Islands",
    "papua new guinea": "Papua New Guinea",
    "french southern and antarctic lands":
      "French Southern and Antarctic Lands",
    tanzania: "United Republic of Tanzania",
    "equatorial guinea": "Equatorial Guinea",
    "dominican republic": "Dominican Republic",
    "republic of serbia": "Republic of Serbia",
    "united states": "United States of America",
    "trinidad and tobago": "Trinidad and Tobago",
    "united arab emirates": "United Arab Emirates",
    "republic of the congo": "Republic of the Congo",
    "bosnia and herzegovina": "Bosnia and Herzegovina",
    "central african republic": "Central African Republic",
    "united states of america": "United States of America",
    "united republic of tanzania": "United Republic of Tanzania",
    "democratic republic of the congo": "Democratic Republic of the Congo",
  };

  constructor() {}

  // ** Center Map on Site ** //
  centerMap(
    zoom: number,
    map: google.maps.Map,
    latlng: google.maps.LatLng | google.maps.LatLngLiteral,
  ) {
    map.setCenter(latlng);
    map.setZoom(zoom);
  }
  // ** Center Map on Site ** //

  // ** Get Active Countries ** //
  public getCountries(sites: SiteModel[]): Set<string> {
    const countries = new Set<string>();

    for (const site of sites ?? []) {
      const countryName = site?.locations?.country?.aliasName;
      if (!countryName) continue;

      const key = countryName.toLowerCase().trim();
      const normalized = this.countries[key] ?? countryName;

      countries.add(normalized);
    }

    // return countries;

    // Sort before returning
    return new Set([...countries].sort((a, b) => a.localeCompare(b)));
  }
  // ** Get Active Countries ** //

  // ** Get Country Counts ** //
  public getCountryCounts(sites: SiteModel[]): Map<string, number> {
    const counts = new Map<string, number>();

    for (const site of sites ?? []) {
      const countryName = site?.locations?.country?.aliasName;
      if (!countryName) continue;

      const key = countryName.toLowerCase().trim();
      const normalized = this.countries[key] ?? countryName;

      counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
    }

    return counts;
  }
  // ** Get Country Counts ** //

  // ** Get Site Icon By Segment ** //
  public getSiteIconBySegment(segment?: string, hasColor?: boolean): string {
    const group =
      (segment && this.SEGMENT_GROUP_MAP[segment]) ?? this.DEFAULT_GROUP;

    return hasColor
      ? this.COLOR_CHANGED_GROUP_ICON_MAP[group]
      : this.GROUP_ICON_MAP[group];
  }
  // ** Get Site Icon By Segment ** //

  // ** Get Site Icon By Group Name ** //
  public getSiteIconByGroupName(group?: string, hasColor?: boolean): string {
    const resolvedGroup = group ?? this.DEFAULT_GROUP;

    return hasColor
      ? this.COLOR_CHANGED_GROUP_ICON_MAP[resolvedGroup]
      : this.GROUP_ICON_MAP[resolvedGroup];
  }
  // ** Get Site Icon By Group Name ** //

  // ** Get Site Icon By Group ** //
  public getSiteIconByGroup(group?: string): string {
    return (
      (group && this.GROUP_ICON_MAP[group]) ??
      this.GROUP_ICON_MAP[this.DEFAULT_GROUP]
    );
  }
  // ** Get Site Icon By Group ** //

  // ** Get Segments By Group ** //
  public getSegmentsByGroup(group: string): string[] {
    return Object.entries(this.SEGMENT_GROUP_MAP)
      .filter(([_, mappedGroup]) => mappedGroup === group)
      .map(([segment]) => segment);
  }
  // ** Get Segments By Group ** //

  // ** Get Group By Segment ** //
  public getGroupBySegment(segment?: string): string {
    return (segment && this.SEGMENT_GROUP_MAP[segment]) ?? this.DEFAULT_GROUP;
  }
  // ** Get Group By Segment ** //

  // ** Get Polygon Center ** //
  polygonCentroid(
    latOffset: number,
    polygon: google.maps.Polygon,
  ): google.maps.LatLngLiteral | null {
    const path = polygon.getPath();

    if (!path || path.getLength() < 1) {
      return null;
    }

    let minLat = path.getAt(0).lat();
    let minLng = path.getAt(0).lng();
    let maxLat = minLat;
    let maxLng = minLng;

    for (let i = 1; i < path.getLength(); i++) {
      const lat = path.getAt(i).lat();
      const lng = path.getAt(i).lng();

      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    }

    return {
      lat: (minLat + maxLat) / 2 + latOffset,
      lng: (minLng + maxLng) / 2,
    };
  }

  // ** Get Polygon Center ** //
}
